"use client";

import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragOverlay,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus,
  MoreHorizontal,
  Pencil,
  Trash2,
  Variable,
  FolderOpen,
  ChevronDown,
  ChevronRight,
  Search,
  FileSpreadsheet,
  GripVertical,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  listAllVariableGroups,
  createVariableGroup,
  updateVariableGroup,
  deleteVariableGroup,
  createVariable,
  updateVariable,
  deleteVariable,
  reorderVariableGroups,
  reorderVariables,
} from "@/lib/api/variables";
import { getErrorMessage } from "@/lib/api/client";
import { slugify } from "@/lib/utils";
import type { VariableGroupWithVariables, Variable as VariableType, VariableType as VarType } from "@/types";

interface VariablesTabProps {
  versionId: number;
  isEditable: boolean;
  openFieldSlug?: string | null;
  onOpenFieldSlugConsumed?: () => void;
}

// Sortable Variable Component
interface SortableVariableProps {
  variable: VariableType;
  isEditable: boolean;
  onEdit: (variable: VariableType) => void;
  onDelete: (id: number) => void;
  getTypeBadge: (type: VarType) => React.ReactNode;
}

function SortableVariable({ variable, isEditable, onEdit, onDelete, getTypeBadge }: SortableVariableProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `variable-${variable.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center justify-between p-3 rounded-lg bg-background"
    >
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {isEditable && (
          <button
            className="cursor-grab active:cursor-grabbing touch-none"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </button>
        )}
        {!isEditable && <GripVertical className="h-4 w-4 text-muted-foreground opacity-30" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p
              className="font-medium cursor-pointer hover:text-primary hover:underline transition-colors"
              onClick={() => onEdit(variable)}
            >
              {variable.name}
            </p>
            {getTypeBadge(variable.var_type)}
            {variable.excel_source && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <FileSpreadsheet className="h-4 w-4 text-success cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <div className="flex items-center gap-2">
                    <FileSpreadsheet className="h-4 w-4 text-success" />
                    <div>
                      <p className="text-xs text-muted-foreground">Source Excel</p>
                      <p className="font-mono text-sm">{variable.excel_source}</p>
                    </div>
                  </div>
                </TooltipContent>
              </Tooltip>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            <code className="px-1 py-0.5 bg-muted rounded text-xs">
              {variable.slug}
            </code>
            <span className="mx-2">=</span>
            <span className="font-mono">{String(variable.value)}</span>
          </p>
        </div>
      </div>

      {isEditable && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEdit(variable)}>
              <Pencil className="mr-2 h-4 w-4" />
              Modifier
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={() => onDelete(variable.id)}
              className="text-destructive focus:text-destructive"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Supprimer
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

// Variable Drag Preview
interface VariableDragPreviewProps {
  variable: VariableType;
  getTypeBadge: (type: VarType) => React.ReactNode;
}

function VariableDragPreview({ variable, getTypeBadge }: VariableDragPreviewProps) {
  return (
    <div className="flex items-center justify-between p-3 rounded-lg bg-background border-2 border-primary shadow-lg opacity-90">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="font-medium">{variable.name}</p>
            {getTypeBadge(variable.var_type)}
          </div>
          <p className="text-sm text-muted-foreground">
            <code className="px-1 py-0.5 bg-muted rounded text-xs">
              {variable.slug}
            </code>
          </p>
        </div>
      </div>
    </div>
  );
}

// Sortable Group Component
interface SortableGroupProps {
  group: VariableGroupWithVariables;
  isEditable: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onEditGroup: (group: VariableGroupWithVariables) => void;
  onDeleteGroup: (id: number) => void;
  onAddVariable: (groupId: number) => void;
  onEditVariable: (variable: VariableType) => void;
  onDeleteVariable: (id: number) => void;
  getTypeBadge: (type: VarType) => React.ReactNode;
  onVariableDragEnd: (groupId: number, activeId: string, overId: string) => void;
  activeVariableId: string | null;
}

function SortableGroup({
  group,
  isEditable,
  isExpanded,
  onToggle,
  onEditGroup,
  onDeleteGroup,
  onAddVariable,
  onEditVariable,
  onDeleteVariable,
  getTypeBadge,
  onVariableDragEnd,
  activeVariableId,
}: SortableGroupProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: `group-${group.id}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const hasVariables = group.variables.length > 0;
  const sortedVariables = useMemo(
    () => [...group.variables].sort((a, b) => a.order - b.order),
    [group.variables]
  );
  const variableIds = useMemo(
    () => sortedVariables.map((v) => `variable-${v.id}`),
    [sortedVariables]
  );

  const variableSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleVariableDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onVariableDragEnd(group.id, String(active.id), String(over.id));
    }
  };

  return (
    <div ref={setNodeRef} style={style} className="border rounded-lg">
      <div
        className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={onToggle}
      >
        <div className="flex items-center gap-3">
          {isEditable && (
            <button
              className="cursor-grab active:cursor-grabbing touch-none"
              onClick={(e) => e.stopPropagation()}
              {...attributes}
              {...listeners}
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
          {!isEditable && <div className="w-4" />}
          {hasVariables ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )
          ) : (
            <div className="w-4" />
          )}
          <FolderOpen className="h-5 w-5 text-muted-foreground" />
          <div>
            <p className="font-medium">{group.name}</p>
            <p className="text-sm text-muted-foreground">
              {group.variables.length} variable{group.variables.length > 1 ? "s" : ""}
            </p>
          </div>
        </div>

        {isEditable && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onAddVariable(group.id);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Ajouter variable
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEditGroup(group);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Modifier
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onDeleteGroup(group.id);
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {isExpanded && (
        <div className="border-t px-4 py-2 bg-muted/30">
          {group.variables.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              Aucune variable dans ce groupe
            </p>
          ) : (
            <div className="space-y-2">
              {isEditable && !activeVariableId ? (
                <DndContext
                  sensors={variableSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleVariableDragEnd}
                >
                  <SortableContext items={variableIds} strategy={verticalListSortingStrategy}>
                    {sortedVariables.map((variable) => (
                      <SortableVariable
                        key={variable.id}
                        variable={variable}
                        isEditable={isEditable}
                        onEdit={onEditVariable}
                        onDelete={onDeleteVariable}
                        getTypeBadge={getTypeBadge}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
              ) : (
                sortedVariables.map((variable) => (
                  <SortableVariable
                    key={variable.id}
                    variable={variable}
                    isEditable={isEditable}
                    onEdit={onEditVariable}
                    onDelete={onDeleteVariable}
                    getTypeBadge={getTypeBadge}
                  />
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Group Drag Preview
function GroupDragPreview({ group }: { group: VariableGroupWithVariables }) {
  return (
    <div className="border-2 border-primary rounded-lg shadow-lg opacity-90 bg-background">
      <div className="flex items-center gap-3 p-4">
        <GripVertical className="h-4 w-4 text-muted-foreground" />
        <FolderOpen className="h-5 w-5 text-muted-foreground" />
        <div>
          <p className="font-medium">{group.name}</p>
          <p className="text-sm text-muted-foreground">
            {group.variables.length} variable{group.variables.length > 1 ? "s" : ""}
          </p>
        </div>
      </div>
    </div>
  );
}

export function VariablesTab({ versionId, isEditable, openFieldSlug, onOpenFieldSlugConsumed }: VariablesTabProps) {
  const queryClient = useQueryClient();
  const [expandedGroups, setExpandedGroups] = useState<Set<number>>(new Set());
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [variableDialogOpen, setVariableDialogOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<VariableGroupWithVariables | null>(null);
  const [selectedVariable, setSelectedVariable] = useState<VariableType | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingOpenSlug, setPendingOpenSlug] = useState<string | null>(null);
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const [activeGroup, setActiveGroup] = useState<VariableGroupWithVariables | null>(null);
  const [activeVariableId, setActiveVariableId] = useState<string | null>(null);
  const [activeVariable, setActiveVariable] = useState<VariableType | null>(null);

  const [groupForm, setGroupForm] = useState({
    name: "",
    slug: "",
    description: "",
    order: 0,
  });

  const [variableForm, setVariableForm] = useState({
    name: "",
    slug: "",
    description: "",
    var_type: "number" as VarType,
    value: "",
    excel_source: "",
    order: 0,
    group_id: 0,
  });

  const { data: groups, isLoading } = useQuery({
    queryKey: ["variable-groups", versionId],
    queryFn: () => listAllVariableGroups(versionId),
  });
  
  // Handle navigation from validation errors - store the slug to open after data loads
  useEffect(() => {
    if (openFieldSlug) {
      setPendingOpenSlug(openFieldSlug);
      onOpenFieldSlugConsumed?.();
    }
  }, [openFieldSlug, onOpenFieldSlugConsumed]);
  
  // Open variable dialog when data is loaded and there's a pending slug
  useEffect(() => {
    if (pendingOpenSlug && groups && !isLoading) {
      // Find the variable by slug
      for (const group of groups) {
        const variable = group.variables.find(v => v.slug === pendingOpenSlug);
        if (variable) {
          // Use setTimeout to ensure state is ready
          setTimeout(() => {
            handleEditVariable(variable);
          }, 100);
          break;
        }
      }
      setPendingOpenSlug(null);
    }
  }, [pendingOpenSlug, groups, isLoading]);

  // Filter groups and variables based on search query
  const filteredGroups = useMemo(() => {
    if (!groups || !searchQuery.trim()) return groups;
    
    const query = searchQuery.toLowerCase().trim();
    
    return groups
      .map((group) => {
        // Filter variables that match the search query
        const matchingVariables = group.variables.filter(
          (variable) =>
            variable.slug.toLowerCase().includes(query) ||
            variable.name.toLowerCase().includes(query)
        );
        
        // Include group if its slug matches or if it has matching variables
        const groupMatches = group.slug.toLowerCase().includes(query) || group.name.toLowerCase().includes(query);
        
        if (groupMatches || matchingVariables.length > 0) {
          return {
            ...group,
            // If group matches, show all variables; otherwise show only matching ones
            variables: groupMatches ? group.variables : matchingVariables,
          };
        }
        return null;
      })
      .filter((group): group is VariableGroupWithVariables => group !== null);
  }, [groups, searchQuery]);

  const invalidateGroups = () => {
    queryClient.invalidateQueries({ queryKey: ["variable-groups", versionId] });
  };

  // Group mutations
  const createGroupMutation = useMutation({
    mutationFn: (data: typeof groupForm) => createVariableGroup(versionId, data),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Groupe créé");
      closeGroupDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const updateGroupMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<typeof groupForm> }) =>
      updateVariableGroup(versionId, id, data),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Groupe mis à jour");
      closeGroupDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteGroupMutation = useMutation({
    mutationFn: (id: number) => deleteVariableGroup(versionId, id),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Groupe supprimé");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Variable mutations
  const createVariableMutation = useMutation({
    mutationFn: (data: typeof variableForm) =>
      createVariable(versionId, {
        ...data,
        value: parseValue(data.value, data.var_type),
      }),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Variable créée");
      closeVariableDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const updateVariableMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<typeof variableForm> }) =>
      updateVariable(versionId, id, {
        ...data,
        value: data.value ? parseValue(data.value, data.var_type || "string") : undefined,
      }),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Variable mise à jour");
      closeVariableDialog();
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  const deleteVariableMutation = useMutation({
    mutationFn: (id: number) => deleteVariable(versionId, id),
    onSuccess: () => {
      invalidateGroups();
      toast.success("Variable supprimée");
    },
    onError: (error) => toast.error("Erreur", { description: getErrorMessage(error) }),
  });

  // Reorder mutations
  const reorderGroupsMutation = useMutation({
    mutationFn: (groupOrders: { id: number; order: number }[]) =>
      reorderVariableGroups(versionId, groupOrders),
    onSuccess: () => {
      invalidateGroups();
    },
    onError: (error) => toast.error("Erreur lors du réordonnancement", { description: getErrorMessage(error) }),
  });

  const reorderVariablesMutation = useMutation({
    mutationFn: (variableOrders: { id: number; order: number }[]) =>
      reorderVariables(versionId, variableOrders),
    onSuccess: () => {
      invalidateGroups();
    },
    onError: (error) => toast.error("Erreur lors du réordonnancement", { description: getErrorMessage(error) }),
  });

  const parseValue = (value: string, type: VarType): unknown => {
    switch (type) {
      case "number":
        return parseFloat(value) || 0;
      case "boolean":
        return value === "true";
      default:
        return value;
    }
  };

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const toggleGroup = (groupId: number) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupId)) {
      newExpanded.delete(groupId);
    } else {
      newExpanded.add(groupId);
    }
    setExpandedGroups(newExpanded);
  };

  const closeGroupDialog = () => {
    setGroupDialogOpen(false);
    setIsEditing(false);
    setSelectedGroup(null);
    setGroupForm({ name: "", slug: "", description: "", order: 0 });
  };

  const closeVariableDialog = () => {
    setVariableDialogOpen(false);
    setIsEditing(false);
    setSelectedVariable(null);
    setVariableForm({
      name: "",
      slug: "",
      description: "",
      var_type: "number",
      value: "",
      excel_source: "",
      order: 0,
      group_id: 0,
    });
  };

  const handleEditGroup = (group: VariableGroupWithVariables) => {
    setSelectedGroup(group);
    setGroupForm({
      name: group.name,
      slug: group.slug,
      description: group.description || "",
      order: group.order,
    });
    setIsEditing(true);
    setGroupDialogOpen(true);
  };

  const handleAddVariable = (groupId: number) => {
    setVariableForm({
      ...variableForm,
      group_id: groupId,
    });
    setVariableDialogOpen(true);
  };

  const handleEditVariable = (variable: VariableType) => {
    setSelectedVariable(variable);
    setVariableForm({
      name: variable.name,
      slug: variable.slug,
      description: variable.description || "",
      var_type: variable.var_type,
      value: String(variable.value),
      excel_source: variable.excel_source || "",
      order: variable.order,
      group_id: variable.group_id,
    });
    setIsEditing(true);
    setVariableDialogOpen(true);
  };

  const getTypeBadge = (type: VarType) => {
    const colors: Record<VarType, string> = {
      string: "bg-success/20 text-success",
      number: "bg-info/20 text-info",
      boolean: "bg-laposte-navy/20 text-laposte-navy",
      formula: "bg-warning/20 text-warning",
    };
    return (
      <Badge className={colors[type] || ""}>{type}</Badge>
    );
  };

  // Handle group drag start
  const handleGroupDragStart = (event: { active: { id: string | number } }) => {
    if (!groups) return;
    const activeIdStr = String(event.active.id);
    
    if (activeIdStr.startsWith("group-")) {
      const groupId = Number(activeIdStr.replace("group-", ""));
      const group = groups.find(g => g.id === groupId);
      setActiveGroupId(activeIdStr);
      setActiveGroup(group || null);
    }
  };

  // Handle group drag end
  const handleGroupDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    setActiveGroupId(null);
    setActiveGroup(null);
    
    if (!over || active.id === over.id || !groups) return;

    const activeIdStr = String(active.id);
    const overIdStr = String(over.id);
    
    if (!activeIdStr.startsWith("group-") || !overIdStr.startsWith("group-")) return;

    const activeGroupIdNum = Number(activeIdStr.replace("group-", ""));
    const overGroupIdNum = Number(overIdStr.replace("group-", ""));

    const sortedGroups = [...groups].sort((a, b) => a.order - b.order);
    const oldIndex = sortedGroups.findIndex((g) => g.id === activeGroupIdNum);
    const newIndex = sortedGroups.findIndex((g) => g.id === overGroupIdNum);

    if (oldIndex === -1 || newIndex === -1) return;

    const newGroups = arrayMove(sortedGroups, oldIndex, newIndex);
    const groupOrders = newGroups.map((g, index) => ({
      id: g.id,
      order: index,
    }));

    reorderGroupsMutation.mutate(groupOrders);
  };

  // Handle variable drag end within a group
  const handleVariableDragEnd = (groupId: number, activeIdStr: string, overIdStr: string) => {
    if (!groups) return;

    const group = groups.find(g => g.id === groupId);
    if (!group) return;

    const activeVarId = Number(activeIdStr.replace("variable-", ""));
    const overVarId = Number(overIdStr.replace("variable-", ""));

    const sortedVars = [...group.variables].sort((a, b) => a.order - b.order);
    const oldIndex = sortedVars.findIndex((v) => v.id === activeVarId);
    const newIndex = sortedVars.findIndex((v) => v.id === overVarId);

    if (oldIndex === -1 || newIndex === -1) return;

    const newVars = arrayMove(sortedVars, oldIndex, newIndex);
    const varOrders = newVars.map((v, index) => ({
      id: v.id,
      order: index,
    }));

    reorderVariablesMutation.mutate(varOrders);
  };

  // Sorted groups for DnD
  const sortedGroups = useMemo(
    () => filteredGroups ? [...filteredGroups].sort((a, b) => a.order - b.order) : [],
    [filteredGroups]
  );
  const groupIds = useMemo(
    () => sortedGroups.map((g) => `group-${g.id}`),
    [sortedGroups]
  );

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-64" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-16 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Variables</CardTitle>
          <CardDescription>
            Variables et constantes utilisées dans les calculs
          </CardDescription>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher par slug..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-[220px]"
            />
          </div>
          {isEditable && (
            <Button onClick={() => setGroupDialogOpen(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Nouveau groupe
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {groups?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Variable className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Aucun groupe de variables</p>
            {isEditable && (
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => setGroupDialogOpen(true)}
              >
                Créer un groupe
              </Button>
            )}
          </div>
        ) : filteredGroups?.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <Search className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>Aucun résultat pour &quot;{searchQuery}&quot;</p>
            <Button
              variant="outline"
              className="mt-4"
              onClick={() => setSearchQuery("")}
            >
              Effacer la recherche
            </Button>
          </div>
        ) : isEditable ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleGroupDragStart}
            onDragEnd={handleGroupDragEnd}
          >
            <SortableContext items={groupIds} strategy={verticalListSortingStrategy}>
              <div className="space-y-3">
                {sortedGroups.map((group) => (
                  <SortableGroup
                    key={group.id}
                    group={group}
                    isEditable={isEditable}
                    isExpanded={expandedGroups.has(group.id)}
                    onToggle={() => toggleGroup(group.id)}
                    onEditGroup={handleEditGroup}
                    onDeleteGroup={(id) => deleteGroupMutation.mutate(id)}
                    onAddVariable={handleAddVariable}
                    onEditVariable={handleEditVariable}
                    onDeleteVariable={(id) => deleteVariableMutation.mutate(id)}
                    getTypeBadge={getTypeBadge}
                    onVariableDragEnd={handleVariableDragEnd}
                    activeVariableId={activeVariableId}
                  />
                ))}
              </div>
            </SortableContext>
            
            {/* Drag overlay for group preview */}
            <DragOverlay>
              {activeGroup ? (
                <GroupDragPreview group={activeGroup} />
              ) : activeVariable ? (
                <VariableDragPreview variable={activeVariable} getTypeBadge={getTypeBadge} />
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          <div className="space-y-3">
            {sortedGroups.map((group) => (
              <SortableGroup
                key={group.id}
                group={group}
                isEditable={isEditable}
                isExpanded={expandedGroups.has(group.id)}
                onToggle={() => toggleGroup(group.id)}
                onEditGroup={handleEditGroup}
                onDeleteGroup={(id) => deleteGroupMutation.mutate(id)}
                onAddVariable={handleAddVariable}
                onEditVariable={handleEditVariable}
                onDeleteVariable={(id) => deleteVariableMutation.mutate(id)}
                getTypeBadge={getTypeBadge}
                onVariableDragEnd={handleVariableDragEnd}
                activeVariableId={activeVariableId}
              />
            ))}
          </div>
        )}
      </CardContent>

      {/* Group Dialog */}
      <Dialog open={groupDialogOpen} onOpenChange={closeGroupDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Modifier le groupe" : "Nouveau groupe"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="group-name">Nom</Label>
              <Input
                id="group-name"
                value={groupForm.name}
                onChange={(e) =>
                  setGroupForm({
                    ...groupForm,
                    name: e.target.value,
                    slug: isEditing ? groupForm.slug : slugify(e.target.value),
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-slug">Slug</Label>
              <Input
                id="group-slug"
                value={groupForm.slug}
                onChange={(e) => setGroupForm({ ...groupForm, slug: e.target.value })}
              />
              {isEditing && (
                <p className="text-xs text-muted-foreground">
                  Attention : modifier le slug peut impacter les références existantes
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="group-description">Description</Label>
              <Textarea
                id="group-description"
                value={groupForm.description}
                onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeGroupDialog}>
              Annuler
            </Button>
            <Button
              onClick={() =>
                isEditing && selectedGroup
                  ? updateGroupMutation.mutate({ id: selectedGroup.id, data: groupForm })
                  : createGroupMutation.mutate(groupForm)
              }
              disabled={createGroupMutation.isPending || updateGroupMutation.isPending}
            >
              {isEditing ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Variable Dialog */}
      <Dialog open={variableDialogOpen} onOpenChange={closeVariableDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {isEditing ? "Modifier la variable" : "Nouvelle variable"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="var-name">Nom</Label>
              <Input
                id="var-name"
                value={variableForm.name}
                onChange={(e) =>
                  setVariableForm({
                    ...variableForm,
                    name: e.target.value,
                    slug: isEditing ? variableForm.slug : slugify(e.target.value),
                  })
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="var-slug">Slug</Label>
              <Input
                id="var-slug"
                value={variableForm.slug}
                onChange={(e) => setVariableForm({ ...variableForm, slug: e.target.value })}
              />
              {isEditing && (
                <p className="text-xs text-muted-foreground">
                  Attention : modifier le slug peut impacter les références existantes
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="var-type">Type</Label>
              <Select
                value={variableForm.var_type}
                onValueChange={(value) =>
                  setVariableForm({ ...variableForm, var_type: value as VarType })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="string">Texte</SelectItem>
                  <SelectItem value="number">Nombre</SelectItem>
                  <SelectItem value="boolean">Booléen</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="var-value">Valeur</Label>
              {variableForm.var_type === "boolean" ? (
                <Select
                  value={variableForm.value}
                  onValueChange={(value) => setVariableForm({ ...variableForm, value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Vrai</SelectItem>
                    <SelectItem value="false">Faux</SelectItem>
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  id="var-value"
                  type={variableForm.var_type === "number" ? "number" : "text"}
                  value={variableForm.value}
                  onChange={(e) => setVariableForm({ ...variableForm, value: e.target.value })}
                />
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="var-description">Description</Label>
              <Textarea
                id="var-description"
                value={variableForm.description}
                onChange={(e) => setVariableForm({ ...variableForm, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="var-excel-source" className="flex items-center gap-2">
                <FileSpreadsheet className="h-4 w-4 text-success" />
                Source Excel
              </Label>
              <Input
                id="var-excel-source"
                value={variableForm.excel_source}
                onChange={(e) => setVariableForm({ ...variableForm, excel_source: e.target.value })}
                placeholder="Ex: 'Costing Transport 2025'!H7"
                className="font-mono text-sm"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="var-order">Ordre au sein du groupe</Label>
              <Input
                id="var-order"
                type="number"
                value={variableForm.order}
                onChange={(e) => setVariableForm({ ...variableForm, order: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeVariableDialog}>
              Annuler
            </Button>
            <Button
              onClick={() =>
                isEditing && selectedVariable
                  ? updateVariableMutation.mutate({ id: selectedVariable.id, data: variableForm })
                  : createVariableMutation.mutate(variableForm)
              }
              disabled={createVariableMutation.isPending || updateVariableMutation.isPending}
            >
              {isEditing ? "Enregistrer" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
